import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    CircularProgress,
    Grid,
    Tooltip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Tabs,
    Tab
} from '@mui/material';
import MainCard from '../../../components/cards/MainCard';
import GlobalDateRangeSelector from '../../../components/GlobalDateRangeSelector';
import GlobalAnimatedProgressBar from '../../../components/GlobalAnimatedProgressBar';
import BusinessIcon from '@mui/icons-material/Workspaces';
import HelpIcon from "@mui/icons-material/HelpOutline";
import CountUp from 'react-countup';
import GlobalPagination from '../../../components/GlobalPagination';
import useDepartmentAnalytics from '../use-data-hooks/useDepartmentAnalytics';
import useLineManagerAnalytics from '../use-data-hooks/useLineManagerAnalytics';
import { getDateRangeDisplayText, calculateDateRange } from '../../../utils/dateRangeUtils';
import { useDynamicBreakpoints } from '../../../utils/breakpointUtils';

const DepartmentOverview = ({ tenancyId }) => {
    const { drawerOpen } = useSelector((state) => state.menu);
    const { matchDownSM, matchDownMD, matchDownLG } = useDynamicBreakpoints(drawerOpen);

    const userLoggedIn = JSON.parse(localStorage.getItem("user"));
    const currentLoggedInUserId = userLoggedIn?.id;

    const isVerySmallScreen = matchDownSM && window.innerWidth <= 375;

    const [dateRange, setDateRange] = useState('6months');
    const [activeTab, setActiveTab] = useState(0); // 0 = Simulations, 1 = Courses
    const [viewTab, setViewTab] = useState(0); // 0 = Teams, 1 = Line Managers, 2 = Department, 3 = Location

    // Custom date range states
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Pagination states
    const [lineManagerPage, setLineManagerPage] = useState(1);
    const [directReportsPage, setDirectReportsPage] = useState(1);
    const [departmentPage, setDepartmentPage] = useState(1);
    const [locationPage, setLocationPage] = useState(1);
    const itemsPerPage = matchDownSM ? 3 : 5;

    const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
    const [selectedDepartment, setSelectedDepartment] = useState('all');
    const [selectedLocation, setSelectedLocation] = useState('all');
    const [selectedLineManager, setSelectedLineManager] = useState('all');

    const dateParams = useMemo(() => {
        const { startDate, endDate } = calculateDateRange(dateRange, customStartDate, customEndDate);
        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };
    }, [dateRange, customStartDate, customEndDate]);

    const { data, isLoading, isError, error, refetch } = useDepartmentAnalytics({
        tenancyId,
        startDate: dateParams.startDate,
        endDate: dateParams.endDate
    });

    // Call 1: Get ALL line managers (for lookup)
    const {
        data: allLineManagersData,
        isLoading: allLineManagersLoading,
        isError: allLineManagersError,
    } = useLineManagerAnalytics({
        tenancyId,
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
    });

    // Call 2: Get current user's direct reports
    const {
        data: currentUserDirectReportsData,
        isLoading: directReportsLoading,
        isError: directReportsError,
    } = useLineManagerAnalytics({
        tenancyId,
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        lineManagerId: currentLoggedInUserId
    }, {
        skip: viewTab !== 1 || !currentLoggedInUserId
    });

    // Check if current user is a line manager and get their line manager ID
    const currentUserAsLineManager = useMemo(() => {
        if (!allLineManagersData?.lineManagerData || !currentLoggedInUserId) return null;

        return allLineManagersData.lineManagerData.find(lm => lm.lineManagerId === currentLoggedInUserId);
    }, [allLineManagersData?.lineManagerData, currentLoggedInUserId]);

    const isCurrentUserLineManager = !!currentUserAsLineManager;

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
    };

    const handleCustomStartDateChange = (event) => {
        setCustomStartDate(event.target.value);
    };

    const handleCustomEndDateChange = (event) => {
        setCustomEndDate(event.target.value);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setDepartmentPage(1);
        setLocationPage(1);
        setLineManagerPage(1);
        setDirectReportsPage(1);
        setSelectedDepartment('all');
        setSelectedLocation('all');
        setSelectedLineManager('all');
        setSelectedTeamFilter('all');
    };

    const handleViewTabChange = (event, newValue) => {
        setViewTab(newValue);
        setDepartmentPage(1);
        setLocationPage(1);
        setLineManagerPage(1);
        setDirectReportsPage(1);
        setSelectedDepartment('all');
        setSelectedLocation('all');
        setSelectedLineManager('all');
        setSelectedTeamFilter('all');
    };

    const handleDepartmentPageChange = (event, value) => {
        setDepartmentPage(value);
    };

    const handleLocationPageChange = (event, value) => {
        setLocationPage(value);
    };

    const handleLineManagerPageChange = (event, value) => {
        setLineManagerPage(value);
    };

    const handleDepartmentFilterChange = (event) => {
        setSelectedDepartment(event.target.value);
        setDepartmentPage(1);
    };

    const handleLocationFilterChange = (event) => {
        setSelectedLocation(event.target.value);
        setLocationPage(1);
    };

    const handleLineManagerFilterChange = (event) => {
        setSelectedLineManager(event.target.value);
        setLineManagerPage(1);
    };

    const handleTeamFilterChange = (event) => {
        setSelectedTeamFilter(event.target.value);
        setLineManagerPage(1);
    };

    const handleDirectReportsPageChange = (event, value) => {
        setDirectReportsPage(value);
    };

    const getFilteredDataForTeamSelection = (dataArray) => {
        if (!dataArray) return [];

        if (selectedTeamFilter === 'your-team' && isCurrentUserLineManager && currentLoggedInUserId) {
            return dataArray.filter(item => item.lineManagerId === currentLoggedInUserId);
        }

        return dataArray;
    };

    // Get unique department and location names for dropdown options
    const departmentOptions = useMemo(() => {
        if (!data?.departmentData) return [];
        const uniqueDepartments = [...new Set(data.departmentData.map(dept => dept.departmentName))];
        return uniqueDepartments.sort();
    }, [data?.departmentData]);

    const locationOptions = useMemo(() => {
        if (!data?.locationData) return [];
        const uniqueLocations = [...new Set(
            data.locationData
                .filter(loc => loc.locationName && loc.locationName.trim() !== '')
                .map(loc => loc.locationName)
        )];
        return uniqueLocations.sort();
    }, [data?.locationData]);

    const lineManagerOptions = useMemo(() => {
        const dataSource = allLineManagersData?.lineManagerData;

        if (!dataSource) return [];

        const filteredData = getFilteredDataForTeamSelection(dataSource);
        const uniqueLineManagers = [...new Set(
            filteredData
                .filter(lm => lm.lineManagerName && lm.lineManagerName.trim() !== '')
                .map(lm => lm.lineManagerName)
        )];
        return uniqueLineManagers.sort();
    }, [allLineManagersData?.lineManagerData, selectedTeamFilter, isCurrentUserLineManager, currentLoggedInUserId]);

    const lineManagerLookup = useMemo(() => {
        if (!allLineManagersData?.lineManagerData) {
            console.log('No all line managers data available');
            return new Map();
        }

        console.log('All line managers data:', allLineManagersData.lineManagerData);

        const lookup = new Map();

        allLineManagersData.lineManagerData.forEach(manager => {
            lookup.set(manager.lineManagerId, {
                isLineManager: true,
                userCount: manager.userCount,
                lineManagerName: manager.lineManagerName,
                lineManagerEmail: manager.lineManagerEmail
            });
        });

        console.log('Line manager lookup from analytics:', lookup);
        return lookup;
    }, [allLineManagersData?.lineManagerData]);

    const reportingManagersCount = useMemo(() => {
        if (viewTab === 0 && currentUserDirectReportsData?.directReportsData && lineManagerLookup.size > 0) {
            return currentUserDirectReportsData.directReportsData.filter(user =>
                lineManagerLookup.has(user.userId)
            ).length;
        }
        return 0;
    }, [currentUserDirectReportsData?.directReportsData, lineManagerLookup, viewTab]);

    // Process department data for the selected content type
    const processedDepartmentData = useMemo(() => {
        if (!data?.departmentData) return [];
        return data.departmentData
            .map(dept => {
                if (activeTab === 0) { // Simulations
                    const clickRate = dept.simulationsSent > 0
                        ? (dept.simulationsClicked / dept.simulationsSent) * 100
                        : 0;
                    return {
                        ...dept,
                        rate: Math.round(clickRate * 100) / 100,
                        displayMetric: dept.simulationsClicked,
                        totalSent: dept.simulationsSent
                    };
                } else { // Courses
                    const completionRate = dept.coursesSent > 0
                        ? (dept.coursesCompleted / dept.coursesSent) * 100
                        : 0;
                    return {
                        ...dept,
                        rate: Math.round(completionRate * 100) / 100,
                        displayMetric: dept.coursesCompleted,
                        totalSent: dept.coursesSent
                    };
                }
            })
            .filter(dept => {
                if (selectedDepartment === 'all') return true;
                return dept.departmentName === selectedDepartment;
            })
            .sort((a, b) => {
                if (activeTab === 0) { // Simulations
                    return b.simulationsClicked - a.simulationsClicked;
                } else { // Courses
                    return b.coursesCompleted - a.coursesCompleted;
                }
            });
    }, [data?.departmentData, activeTab, selectedDepartment]);

    // Process location data for the selected content type
    const processedLocationData = useMemo(() => {
        if (!data?.locationData) return [];

        return data.locationData
            .filter(loc => loc.locationName && loc.locationName.trim() !== '')
            .map(loc => {
                if (activeTab === 0) { // Simulations
                    const clickRate = loc.simulationsSent > 0
                        ? (loc.simulationsClicked / loc.simulationsSent) * 100
                        : 0;

                    return {
                        ...loc,
                        rate: Math.round(clickRate * 100) / 100,
                        displayMetric: loc.simulationsClicked,
                        totalSent: loc.simulationsSent
                    };
                } else { // Courses
                    const completionRate = loc.coursesSent > 0
                        ? (loc.coursesCompleted / loc.coursesSent) * 100
                        : 0;

                    return {
                        ...loc,
                        rate: Math.round(completionRate * 100) / 100,
                        displayMetric: loc.coursesCompleted,
                        totalSent: loc.coursesSent
                    };
                }
            })
            .filter(loc => {
                if (selectedLocation === 'all') return true;
                return loc.locationName === selectedLocation;
            })
            .sort((a, b) => {
                if (activeTab === 0) { // Simulations
                    return b.simulationsClicked - a.simulationsClicked;
                } else { // Courses
                    return b.coursesCompleted - a.coursesCompleted;
                }
            });
    }, [data?.locationData, activeTab, selectedLocation]);

    const processedLineManagerData = useMemo(() => {
        const dataSource = allLineManagersData?.lineManagerData;

        if (!dataSource) return [];

        const filteredData = getFilteredDataForTeamSelection(dataSource);

        return filteredData
            .map(lm => {
                if (activeTab === 0) { // Simulations
                    const clickRate = lm.simulationsSent > 0
                        ? (lm.simulationsClicked / lm.simulationsSent) * 100
                        : 0;

                    return {
                        ...lm,
                        rate: Math.round(clickRate * 100) / 100,
                        displayMetric: lm.simulationsClicked,
                        totalSent: lm.simulationsSent
                    };
                } else { // Courses
                    const completionRate = lm.coursesSent > 0
                        ? (lm.coursesCompleted / lm.coursesSent) * 100
                        : 0;

                    return {
                        ...lm,
                        rate: Math.round(completionRate * 100) / 100,
                        displayMetric: lm.coursesCompleted,
                        totalSent: lm.coursesSent
                    };
                }
            })
            .filter(lm => {
                if (selectedLineManager === 'all') return true;
                return lm.lineManagerName === selectedLineManager;
            })
            .sort((a, b) => {
                if (activeTab === 0) { // Simulations
                    return b.simulationsClicked - a.simulationsClicked;
                } else { // Courses
                    return b.coursesCompleted - a.coursesCompleted;
                }
            });
    }, [allLineManagersData?.lineManagerData, activeTab, selectedLineManager, selectedTeamFilter, isCurrentUserLineManager, currentLoggedInUserId]);

    const processedDirectReportsData = useMemo(() => {
        if (!currentUserDirectReportsData?.directReportsData || viewTab !== 1 || !lineManagerLookup.size) {
            console.log('Debug - processedDirectReportsData conditions:', {
                hasDirectReportsData: !!currentUserDirectReportsData?.directReportsData,
                isLineManagerTab: viewTab === 1,
                hasLineManagerLookup: lineManagerLookup.size > 0,
                directReportsCount: currentUserDirectReportsData?.directReportsData?.length || 0
            });
            return [];
        }

        console.log('Direct reports data:', currentUserDirectReportsData.directReportsData);
        console.log('Line manager lookup:', lineManagerLookup);

        // Filter direct reports to only include those who are line managers
        const lineManagerDirectReports = currentUserDirectReportsData.directReportsData.filter(user => {
            const isLineManager = lineManagerLookup.has(user.userId);
            console.log(`Checking if ${user.userName} (${user.userId}) is a line manager:`, isLineManager);
            return isLineManager;
        });

        console.log('Filtered line manager direct reports:', lineManagerDirectReports);

        return lineManagerDirectReports
            .map(user => {
                const lineManagerInfo = lineManagerLookup.get(user.userId);

                if (activeTab === 0) { // Simulations
                    const clickRate = user.simulationsSent > 0
                        ? (user.simulationsClicked / user.simulationsSent) * 100
                        : 0;
                    return {
                        ...user,
                        rate: Math.round(clickRate * 100) / 100,
                        displayMetric: user.simulationsClicked,
                        totalSent: user.simulationsSent,
                        userCount: lineManagerInfo?.userCount || 0,
                        isLineManager: true
                    };
                } else { // Courses
                    const completionRate = user.coursesSent > 0
                        ? (user.coursesCompleted / user.coursesSent) * 100
                        : 0;
                    return {
                        ...user,
                        rate: Math.round(completionRate * 100) / 100,
                        displayMetric: user.coursesCompleted,
                        totalSent: user.coursesSent,
                        userCount: lineManagerInfo?.userCount || 0,
                        isLineManager: true
                    };
                }
            })
            .sort((a, b) => {
                if (activeTab === 0) {
                    return b.simulationsClicked - a.simulationsClicked;
                } else {
                    return b.coursesCompleted - a.coursesCompleted;
                }
            });
    }, [currentUserDirectReportsData?.directReportsData, activeTab, viewTab, lineManagerLookup]);

    // Get visible departments and locations for current page
    const visibleDepartments = useMemo(() => {
        const start = (departmentPage - 1) * itemsPerPage;
        return processedDepartmentData.slice(start, start + itemsPerPage);
    }, [processedDepartmentData, departmentPage, itemsPerPage]);

    const visibleLocations = useMemo(() => {
        const start = (locationPage - 1) * itemsPerPage;
        return processedLocationData.slice(start, start + itemsPerPage);
    }, [processedLocationData, locationPage, itemsPerPage]);

    const visibleLineManagers = useMemo(() => {
        const start = (lineManagerPage - 1) * itemsPerPage;
        return processedLineManagerData.slice(start, start + itemsPerPage);
    }, [processedLineManagerData, lineManagerPage, itemsPerPage]);

    const visibleDirectReports = useMemo(() => {
        const start = (directReportsPage - 1) * itemsPerPage;
        return processedDirectReportsData.slice(start, start + itemsPerPage);
    }, [processedDirectReportsData, directReportsPage, itemsPerPage]);

    const overallStats = useMemo(() => {
        // Use appropriate data based on viewTab
        const summaryData = viewTab === 0 ? allLineManagersData?.summary : data?.summary;

        if (!summaryData) return {
            totalDepartments: 0,
            totalLocations: 0,
            totalLineManagers: 0,
            totalUsers: 0,
            totalSent: 0,
            totalInteracted: 0,
            reportingManagers: 0
        };

        if (viewTab === 0 && selectedTeamFilter === 'your-team' && isCurrentUserLineManager) {
            const currentViewData = processedLineManagerData || [];

            const totalSent = currentViewData.reduce((sum, item) => sum + (item.totalSent || 0), 0);
            const totalInteracted = currentViewData.reduce((sum, item) => sum + (item.displayMetric || 0), 0);

            return {
                totalDepartments: currentUserAsLineManager ? 1 : 0,
                totalLocations: currentUserAsLineManager ? 1 : 0,
                totalLineManagers: currentViewData.length,
                totalUsers: currentUserAsLineManager?.userCount || 0,
                totalSent,
                totalInteracted,
                reportingManagers: currentViewData.length
            };
        }

        const summary = summaryData;

        if (activeTab === 0) { // Simulations
            return {
                totalDepartments: summary.totalDepartments || 0,
                totalLocations: summary.totalLocations || 0,
                totalLineManagers: summary.totalLineManagers || 0,
                totalUsers: summary.totalUsers || 0,
                totalSent: summary.totalSimulationsSent || 0,
                totalInteracted: summary.totalSimulationsClicked || 0,
                reportingManagers: reportingManagersCount
            };
        } else { // Courses
            return {
                totalDepartments: summary.totalDepartments || 0,
                totalLocations: summary.totalLocations || 0,
                totalLineManagers: summary.totalLineManagers || 0,
                totalUsers: summary.totalUsers || 0,
                totalSent: summary.totalCoursesSent || 0,
                totalInteracted: summary.totalCoursesCompleted || 0,
                reportingManagers: reportingManagersCount
            };
        }
    }, [data?.summary, allLineManagersData?.summary, activeTab, viewTab, selectedTeamFilter, isCurrentUserLineManager,
        processedLineManagerData, currentUserAsLineManager, reportingManagersCount]);

    const currentData = viewTab === 0 ? processedLineManagerData :
        viewTab === 1 ? processedDirectReportsData :
            viewTab === 2 ? processedDepartmentData : processedLocationData;

    const visibleData = viewTab === 0 ? visibleLineManagers :
        viewTab === 1 ? visibleDirectReports :
            viewTab === 2 ? visibleDepartments : visibleLocations;

    const currentPage = viewTab === 0 ? lineManagerPage :
        viewTab === 1 ? directReportsPage :
            viewTab === 2 ? departmentPage : locationPage;

    const currentFilter = viewTab === 0 ? selectedLineManager :
        viewTab === 2 ? selectedDepartment : selectedLocation;

    const filterOptions = viewTab === 0 ? lineManagerOptions :
        viewTab === 2 ? departmentOptions : locationOptions;

    const handlePageChange = viewTab === 0 ? handleLineManagerPageChange :
        viewTab === 1 ? handleDirectReportsPageChange :
            viewTab === 2 ? handleDepartmentPageChange : handleLocationPageChange;

    const handleFilterChange = viewTab === 0 ? handleLineManagerFilterChange :
        viewTab === 2 ? handleDepartmentFilterChange : handleLocationFilterChange;

    if (isLoading ||
        ((viewTab === 0 || viewTab === 1) && allLineManagersLoading) ||
        (viewTab === 1 && directReportsLoading)) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={true}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" color="text.secondary" sx={{ ml: 2 }}>
                        Loading analytics data...
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    if (isError ||
        (viewTab === 0 && allLineManagersError) ||
        (viewTab === 1 && (allLineManagersError || directReportsError))) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={true}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <Typography variant="h6" color="error">
                        Error loading department analytics
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    return (
        <MainCard
            content={false}
            sx={{
                width: "100%",
                bgcolor: "background.default",
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
            border={false}
            boxShadow={true}
        >
            <Box sx={{ p: isVerySmallScreen ? 1 : (matchDownSM ? 1.5 : 2), flexGrow: 1 }}>
                {/* Header Section */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: matchDownSM ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: matchDownSM ? 'flex-start' : 'center',
                    mb: 2,
                    gap: matchDownSM ? 1.5 : 2
                }}>
                    <Typography
                        variant={matchDownSM ? "subtitle1" : "h6"}
                        sx={{
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            flex: '0 0 auto'
                        }}
                    >
                        <BusinessIcon sx={{ mr: 0.5, fontSize: matchDownSM ? '1rem' : '1.2rem' }} color="primary" />
                        Overview {viewTab === 0 && selectedTeamFilter === 'your-team' && isCurrentUserLineManager && <Chip label="My Team" size="small" color="primary" sx={{ ml: 1 }} />}
                    </Typography>

                    <Box sx={{
                        flex: matchDownSM ? '1 1 auto' : '0 0 auto',
                        minWidth: matchDownSM ? '100%' : '200px',
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexDirection: matchDownSM ? 'column' : 'row'
                    }}>
                        <Box sx={{ minWidth: matchDownSM ? '100%' : 300 }}>
                            <GlobalDateRangeSelector
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                onCustomStartDateChange={handleCustomStartDateChange}
                                onCustomEndDateChange={handleCustomEndDateChange}
                                customStartDate={customStartDate}
                                customEndDate={customEndDate}
                                includeCustom={true}
                                fullWidth
                                size="small"
                            />
                        </Box>

                        <Tooltip title={selectedTeamFilter === 'your-team' && isCurrentUserLineManager
                            ? "View your team's simulation and course performance across departments and locations."
                            : "View department and location-level simulation and course performance across your organization."
                        }>
                            <IconButton size="small">
                                <HelpIcon sx={{ color: "secondary.main" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Current date range indicator */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Current: {getDateRangeDisplayText(dateRange, customStartDate, customEndDate)}
                </Typography>

                {/* Simulations vs Courses Tabs */}
                <Box sx={{ mb: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant={matchDownSM ? "fullWidth" : "standard"}
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab
                            label="Simulations"
                        />
                        <Tab
                            label="Courses"
                        />
                    </Tabs>
                </Box>

                {/* Overall Statistics */}
                <Grid container spacing={isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 2)} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={6} md={2.4}>
                        <MainCard sx={{
                            textAlign: 'center',
                            bgcolor: 'background.default',
                            p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                        }}>
                            <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                <CountUp
                                    start={0}
                                    end={viewTab === 0 && selectedTeamFilter === 'all' ? overallStats.totalLineManagers : overallStats.totalDepartments}
                                    duration={1}
                                    preserveValue
                                />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {viewTab === 0 && selectedTeamFilter === 'all' ? 'Teams' : 'Departments'}
                            </Typography>
                        </MainCard>
                    </Grid>
                    <Grid item xs={6} sm={6} md={2.4}>
                        <MainCard sx={{
                            textAlign: 'center',
                            bgcolor: 'background.default',
                            p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                        }}>
                            <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                <CountUp
                                    start={0}
                                    end={viewTab === 0 && selectedTeamFilter === 'all' ? overallStats.reportingManagers : overallStats.totalLocations}
                                    duration={1.1}
                                    preserveValue
                                />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {viewTab === 0 && selectedTeamFilter === 'all' ? 'Direct Line Managers' : 'Locations'}
                            </Typography>
                        </MainCard>
                    </Grid>
                    <Grid item xs={6} sm={6} md={2.4}>
                        <MainCard sx={{
                            textAlign: 'center',
                            bgcolor: 'background.default',
                            p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                        }}>
                            <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                <CountUp
                                    start={0}
                                    end={overallStats.totalUsers}
                                    duration={1.5}
                                    preserveValue
                                />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Users</Typography>
                        </MainCard>
                    </Grid>
                    <Grid item xs={6} sm={6} md={2.4}>
                        <MainCard sx={{
                            textAlign: 'center',
                            bgcolor: 'background.default',
                            p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                        }}>
                            <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                <CountUp
                                    start={0}
                                    end={overallStats.totalSent}
                                    duration={1.2}
                                    preserveValue
                                />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {activeTab === 0 ? 'Sims Sent' : 'Courses Sent'}
                            </Typography>
                        </MainCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <MainCard sx={{
                            textAlign: 'center',
                            bgcolor: 'background.default',
                            p: isVerySmallScreen ? 0.5 : (matchDownSM ? 1 : 1.5)
                        }}>
                            <Typography variant={matchDownSM ? "body1" : "h6"} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                <CountUp
                                    start={0}
                                    end={overallStats.totalInteracted}
                                    duration={1.8}
                                    preserveValue
                                />
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {activeTab === 0 ? 'Sims Clicked' : 'Courses Completed'}
                            </Typography>
                        </MainCard>
                    </Grid>
                </Grid>

                <Box sx={{ mb: 2 }}>
                    <Tabs
                        value={viewTab}
                        onChange={handleViewTabChange}
                        variant={matchDownSM ? "fullWidth" : "standard"}
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab
                            label="Teams"
                        />
                        <Tab
                            label="Line Managers"
                        />
                        <Tab
                            label="Department"
                        />
                        <Tab
                            label="Location"
                        />
                    </Tabs>
                </Box>

                {/* Main Content Section */}
                <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={false}>
                    <Box sx={{ p: isVerySmallScreen ? 1 : (matchDownSM ? 1.5 : 2) }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: matchDownSM ? 'flex-start' : 'center',
                            mb: 2,
                            flexDirection: matchDownSM ? 'column' : 'row',
                            gap: matchDownSM ? 2 : 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant={matchDownSM ? "body1" : "subtitle1"} sx={{ fontWeight: 600 }}>
                                    {activeTab === 0 ? 'Simulation' : 'Course'} Performance by {
                                        viewTab === 0 ? 'Team' :
                                            viewTab === 1 ? 'Line Managers in my Team' :
                                                viewTab === 2 ? 'Department' :
                                                    'Location'
                                    }
                                    {viewTab === 0 && selectedTeamFilter === 'your-team' && isCurrentUserLineManager &&
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            (My Team Only)
                                        </Typography>
                                    }
                                </Typography>
                                <Chip
                                    label={currentData.length}
                                    size="small"
                                    color="primary"
                                />
                                {viewTab === 1 && (
                                    <Tooltip title={"View directly reporting line managers that belong to your team. A full tree can be viewed under Users > Additional Data > Line Manager."}>
                                        <IconButton size="small">
                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {currentData.length > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: matchDownSM ? 'column' : 'row',
                                    width: matchDownSM ? '100%' : 'auto',
                                    alignItems: 'center'
                                }}>
                                    {viewTab === 0 && (
                                        <FormControl size="small" sx={{ minWidth: matchDownSM ? '100%' : '200px' }}>
                                            <InputLabel id="team-scope-filter-label">
                                                Team Scope
                                            </InputLabel>
                                            <Select
                                                labelId="team-scope-filter-label"
                                                value={selectedTeamFilter}
                                                onChange={handleTeamFilterChange}
                                                label="Team Scope"
                                            >
                                                <MenuItem value="all">All Teams</MenuItem>
                                                {isCurrentUserLineManager && (
                                                    <MenuItem value="your-team">Your Team</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {(viewTab === 2 || viewTab === 3) && (
                                        <FormControl size="small" sx={{ minWidth: matchDownSM ? '100%' : '250px' }}>
                                            <InputLabel id={`${viewTab === 2 ? 'department' : 'location'}-filter-label`}>
                                                Filter by {viewTab === 2 ? 'Department' : 'Location'}
                                            </InputLabel>
                                            <Select
                                                labelId={`${viewTab === 2 ? 'department' : 'location'}-filter-label`}
                                                value={currentFilter}
                                                onChange={handleFilterChange}
                                                label={`Filter by ${viewTab === 2 ? 'Department' : 'Location'}`}
                                            >
                                                <MenuItem value="all">All {viewTab === 2 ? 'Departments' : 'Locations'}</MenuItem>
                                                {filterOptions.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {currentData.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                                {viewTab === 1
                                    ? "No line managers found in your team for the selected date range."
                                    : `No ${activeTab === 0 ? 'simulation' : 'course'} data available for ${viewTab === 0 ? 'teams' : viewTab === 2 ? 'departments' : 'locations'}.`
                                }
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {viewTab === 1 ?
                                            `Showing all ${currentData.length} directly reporting line managers` :
                                            currentFilter === 'all' ?
                                                `Showing ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, currentData.length)} of ${currentData.length}` :
                                                `Showing ${currentData.length} results for "${currentFilter}"`
                                        }
                                    </Typography>
                                </Box>

                                {visibleData.length === 0 && currentFilter !== 'all' && viewTab !== 1 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            No data found for {viewTab === 0 ? 'team' : viewTab === 2 ? 'department' : 'location'} "{currentFilter}" in the selected date range.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        {visibleData.map((item) => (
                                            <MainCard key={viewTab === 0 ? item.lineManagerId :
                                                viewTab === 1 ? item.userId :
                                                    viewTab === 2 ? item.departmentId : item.locationId}
                                                sx={{ mb: 2, bgcolor: "background.default" }} content={false}>
                                                <Box sx={{ p: isVerySmallScreen ? 1 : (matchDownSM ? 1.5 : 2) }}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        mb: matchDownSM ? 2 : 1,
                                                        gap: 1
                                                    }}>
                                                        {/* Department/Location/Line Manager Name */}
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            mb: 1
                                                        }}>
                                                            <Typography
                                                                variant={isVerySmallScreen ? "body2" : (matchDownSM ? "body1" : "subtitle2")}
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    flex: 1,
                                                                    pr: 1,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {viewTab === 0 ?
                                                                    `${(item.lineManagerName || '').trim()}'s Team` :
                                                                    viewTab === 1 ?
                                                                        `${item.userName || item.userEmail || 'Unknown User'}` :
                                                                        viewTab === 2 ? item.departmentName :
                                                                            item.locationName
                                                                }
                                                            </Typography>

                                                            {/* Performance Indicator */}
                                                            <Box sx={{
                                                                textAlign: 'right',
                                                                flex: '0 0 auto'
                                                            }}>
                                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                    {activeTab === 0 ? 'Click Rate' : 'Completion Rate'}
                                                                </Typography>
                                                                <Typography variant={isVerySmallScreen ? "caption" : "body2"} fontWeight="bold">
                                                                    {item.rate.toFixed(1)}%
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Chips - Updated to show team size for line managers */}
                                                        <Box sx={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            gap: isVerySmallScreen ? 0.5 : 1,
                                                            flexWrap: 'wrap',
                                                            '& .MuiChip-root': {
                                                                minWidth: 0,
                                                                flex: isVerySmallScreen ? '1 1 auto' : '0 0 auto'
                                                            }
                                                        }}>
                                                            {/* Show team size for line managers (viewTab === 1) */}
                                                            {viewTab === 1 && item.userCount !== undefined && (
                                                                <Chip
                                                                    label={`Manages ${item.userCount} ${item.userCount === 1 ? 'person' : 'people'}`}
                                                                    size="small"
                                                                    color="info"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
                                                                        height: isVerySmallScreen ? 20 : 24,
                                                                        '& .MuiChip-label': {
                                                                            px: isVerySmallScreen ? 0.5 : 1
                                                                        }
                                                                    }}
                                                                />
                                                            )}

                                                            <Chip
                                                                label={`${item.totalSent || 0} sent`}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{
                                                                    fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
                                                                    height: isVerySmallScreen ? 20 : 24,
                                                                    '& .MuiChip-label': {
                                                                        px: isVerySmallScreen ? 0.5 : 1
                                                                    }
                                                                }}
                                                            />
                                                            <Chip
                                                                label={`${item.displayMetric || 0} ${activeTab === 0 ? 'clicked' : 'completed'}`}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                                sx={{
                                                                    fontSize: isVerySmallScreen ? '0.6rem' : '0.75rem',
                                                                    height: isVerySmallScreen ? 20 : 24,
                                                                    '& .MuiChip-label': {
                                                                        px: isVerySmallScreen ? 0.5 : 1
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>

                                                    {/* Performance Bar */}
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <GlobalAnimatedProgressBar
                                                                percentage={item.rate}
                                                                backgroundColor="grey.200"
                                                                colorScheme={activeTab === 0 ? "gradient" : "primary"}
                                                                showLabel={false}
                                                                showPercentage={false}
                                                                showCounts={false}
                                                                textPosition="none"
                                                                showShineEffect={true}
                                                                animationDuration={1.2}
                                                                triggerAnimation={true}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </MainCard>
                                        ))}

                                        {visibleData.length > 0 && currentData.length > itemsPerPage && viewTab !== 1 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                <GlobalPagination
                                                    count={Math.ceil(currentData.length / itemsPerPage)}
                                                    page={currentPage}
                                                    onChange={handlePageChange}
                                                    showFirstButton={false}
                                                    showLastButton={false}
                                                    size={matchDownSM ? "small" : "medium"}
                                                />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </MainCard>
            </Box>
        </MainCard>
    );
};

export default DepartmentOverview;